/**
 * Pure donut geometry — no React / react-native-svg imports, so it's unit-
 * testable without the RN render harness.
 */

/** One rendered donut segment: a sector `arc`, or a `circle` for the single
 *  full-circle case (whose 360° arc would be degenerate). */
export type PieShape =
	| { index: number; kind: 'circle' }
	| { index: number; kind: 'arc'; d: string };

/**
 * Build the donut segments for a set of slice weights. Non-finite weights
 * coerce to 0 so a bad input (e.g. a missing KPI field arriving `undefined`)
 * can't poison `total` and slip past the empty guard into NaN SVG coordinates.
 * When one slice holds the entire total it becomes a `circle` — an arc from a
 * point back to itself is degenerate and crashes react-native-svg on web.
 */
export function buildPieShapes(
	values: number[],
	geom: { cx: number; cy: number; r: number },
): { total: number; shapes: PieShape[] } {
	const { cx, cy, r } = geom;
	const safe = values.map((v) => (Number.isFinite(v) ? Math.max(0, v) : 0));
	const total = safe.reduce((s, v) => s + v, 0);
	const shapes: PieShape[] = [];
	if (!(total > 0)) return { total: 0, shapes };

	let running = 0;
	for (let i = 0; i < safe.length; i++) {
		const v = safe[i];
		if (v === 0) continue;
		if (v >= total) {
			shapes.push({ index: i, kind: 'circle' });
			continue;
		}
		const startAngle = (running / total) * 2 * Math.PI;
		running += v;
		const endAngle = (running / total) * 2 * Math.PI;
		const x1 = cx + r * Math.sin(startAngle);
		const y1 = cy - r * Math.cos(startAngle);
		const x2 = cx + r * Math.sin(endAngle);
		const y2 = cy - r * Math.cos(endAngle);
		const large = endAngle - startAngle > Math.PI ? 1 : 0;
		shapes.push({ index: i, kind: 'arc', d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z` });
	}
	return { total, shapes };
}
